import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-smokers-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Smoker Rate Finder ─────────────────────────────────────────────────── */

type SmokerAge = '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60';
type SmokerCoverage = '100k' | '250k' | '500k' | '750k' | '1m';
type SmokerTerm = '10' | '15' | '20' | '25' | '30';
type QuitStatus = 'current' | 'quit1yr' | 'quit2yr' | 'quit4yr' | 'never';

interface SmokerRateResult {
  carrier: string;
  amBest: string;
  monthlyRate: number;
  annualRate: number;
  rateClass: string;
  termYears: number;
  coverage: number;
  notes: string;
  badge?: string;
}

const RATE_TABLE: Record<SmokerAge, Record<SmokerCoverage, Record<SmokerTerm, number>>> = {
  '25': { '100k': { '10': 18, '15': 22, '20': 26, '25': 30, '30': 34 }, '250k': { '10': 32, '15': 40, '20': 48, '25': 56, '30': 64 }, '500k': { '10': 56, '15': 70, '20': 84, '25': 98, '30': 112 }, '750k': { '10': 80, '15': 100, '20': 120, '25': 140, '30': 160 }, '1m': { '10': 100, '15': 126, '20': 152, '25': 178, '30': 204 } },
  '30': { '100k': { '10': 22, '15': 28, '20': 34, '25': 40, '30': 46 }, '250k': { '10': 42, '15': 54, '20': 66, '25': 78, '30': 90 }, '500k': { '10': 76, '15': 98, '20': 120, '25': 142, '30': 164 }, '750k': { '10': 108, '15': 140, '20': 172, '25': 204, '30': 236 }, '1m': { '10': 136, '15': 176, '20': 216, '25': 256, '30': 296 } },
  '35': { '100k': { '10': 28, '15': 36, '20': 44, '25': 52, '30': 60 }, '250k': { '10': 56, '15': 72, '20': 88, '25': 104, '30': 120 }, '500k': { '10': 102, '15': 132, '20': 162, '25': 192, '30': 222 }, '750k': { '10': 146, '15': 190, '20': 234, '25': 278, '30': 322 }, '1m': { '10': 184, '15': 240, '20': 296, '25': 352, '30': 408 } },
  '40': { '100k': { '10': 38, '15': 50, '20': 62, '25': 74, '30': 86 }, '250k': { '10': 78, '15': 102, '20': 126, '25': 150, '30': 174 }, '500k': { '10': 146, '15': 192, '20': 238, '25': 284, '30': 330 }, '750k': { '10': 210, '15': 276, '20': 342, '25': 408, '30': 474 }, '1m': { '10': 266, '15': 350, '20': 434, '25': 518, '30': 602 } },
  '45': { '100k': { '10': 56, '15': 74, '20': 92, '25': 110, '30': 128 }, '250k': { '10': 118, '15': 156, '20': 194, '25': 232, '30': 270 }, '500k': { '10': 226, '15': 300, '20': 374, '25': 448, '30': 522 }, '750k': { '10': 326, '15': 432, '20': 538, '25': 644, '30': 750 }, '1m': { '10': 414, '15': 550, '20': 686, '25': 822, '30': 958 } },
  '50': { '100k': { '10': 82, '15': 110, '20': 138, '25': 166, '30': 194 }, '250k': { '10': 176, '15': 236, '20': 296, '25': 356, '30': 416 }, '500k': { '10': 340, '15': 456, '20': 572, '25': 688, '30': 804 }, '750k': { '10': 490, '15': 658, '20': 826, '25': 994, '30': 1162 }, '1m': { '10': 624, '15': 838, '20': 1052, '25': 1266, '30': 1480 } },
  '55': { '100k': { '10': 122, '15': 164, '20': 206, '25': 248, '30': 290 }, '250k': { '10': 264, '15': 356, '20': 448, '25': 540, '30': 632 }, '500k': { '10': 514, '15': 694, '20': 874, '25': 1054, '30': 1234 }, '750k': { '10': 742, '15': 1002, '20': 1262, '25': 1522, '30': 1782 }, '1m': { '10': 946, '15': 1278, '20': 1610, '25': 1942, '30': 2274 } },
  '60': { '100k': { '10': 182, '15': 246, '20': 310, '25': 374, '30': 438 }, '250k': { '10': 396, '15': 536, '20': 676, '25': 816, '30': 956 }, '500k': { '10': 774, '15': 1048, '20': 1322, '25': 1596, '30': 1870 }, '750k': { '10': 1118, '15': 1514, '20': 1910, '25': 2306, '30': 2702 }, '1m': { '10': 1424, '15': 1930, '20': 2436, '25': 2942, '30': 3448 } },
};

const COVERAGE_MAP: Record<SmokerCoverage, number> = {
  '100k': 100000, '250k': 250000, '500k': 500000, '750k': 750000, '1m': 1000000,
};

function getSmokerRates(
  age: SmokerAge,
  coverage: SmokerCoverage,
  term: SmokerTerm,
  quitStatus: QuitStatus
): SmokerRateResult[] {
  const baseRate = RATE_TABLE[age][coverage][term];
  const coverageNum = COVERAGE_MAP[coverage];

  let multiplier = 1.0;
  let rateClass = 'Tobacco/Smoker';
  let notes = 'Standard smoker rates — all carriers';

  if (quitStatus === 'quit4yr') {
    multiplier = 0.55;
    rateClass = 'Non-Tobacco (Preferred)';
    notes = 'Quit 4+ years ago — most carriers reclassify as non-smoker. Significant savings!';
  } else if (quitStatus === 'quit2yr') {
    multiplier = 0.70;
    rateClass = 'Non-Tobacco (Standard)';
    notes = 'Quit 2–3 years ago — some carriers offer non-smoker rates. We\'ll find the best.';
  } else if (quitStatus === 'quit1yr') {
    multiplier = 0.85;
    rateClass = 'Tobacco (Preferred)';
    notes = 'Quit 1–2 years ago — still classified as tobacco user by most carriers.';
  } else if (quitStatus === 'never') {
    multiplier = 0.50;
    rateClass = 'Non-Tobacco (Preferred Plus)';
    notes = 'Never smoked — best available rates. Shown for comparison.';
  }

  const adjustedRate = Math.round(baseRate * multiplier);

  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      monthlyRate: adjustedRate,
      annualRate: adjustedRate * 12,
      rateClass,
      termYears: parseInt(term),
      coverage: coverageNum,
      notes,
      badge: 'Best Smoker Rates',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      monthlyRate: Math.round(adjustedRate * 1.04),
      annualRate: Math.round(adjustedRate * 1.04) * 12,
      rateClass,
      termYears: parseInt(term),
      coverage: coverageNum,
      notes,
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      monthlyRate: Math.round(adjustedRate * 1.07),
      annualRate: Math.round(adjustedRate * 1.07) * 12,
      rateClass,
      termYears: parseInt(term),
      coverage: coverageNum,
      notes,
    },
    {
      carrier: 'Transamerica',
      amBest: 'A',
      monthlyRate: Math.round(adjustedRate * 1.10),
      annualRate: Math.round(adjustedRate * 1.10) * 12,
      rateClass,
      termYears: parseInt(term),
      coverage: coverageNum,
      notes: quitStatus === 'quit2yr' ? 'May offer non-smoker rates at 2 years — ask us to check' : notes,
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyRate: Math.round(adjustedRate * 1.13),
      annualRate: Math.round(adjustedRate * 1.13) * 12,
      rateClass,
      termYears: parseInt(term),
      coverage: coverageNum,
      notes,
    },
  ];
}

/* ─── FAQs ───────────────────────────────────────────────────────────────── */

const FAQS = [
  {
    q: 'Can smokers get life insurance in Indianapolis?',
    a: 'Yes — smokers can absolutely get life insurance in Indianapolis. Every major life insurance carrier offers coverage to smokers, though at higher rates than non-smokers. Smoker life insurance rates in Indianapolis are typically 2–4x higher than non-smoker rates for the same coverage amount and term. For example, a 40-year-old male smoker in Indianapolis pays approximately $126–$238/month for $500,000 of 20-year term life insurance, compared to $28–$42/month for a non-smoker. Despite the higher cost, life insurance is still very affordable for most Indianapolis smokers — and it\'s critical protection for your family. An independent agent can compare all available carriers to find the best smoker rates in Indianapolis.',
  },
  {
    q: 'How much does life insurance cost for smokers in Indianapolis?',
    a: 'Life insurance for smokers in Indianapolis costs 2–4x more than non-smoker rates. Here are sample monthly rates for a $500,000 20-year term policy for Indianapolis smokers: Age 30: $120–$164/month. Age 35: $162–$222/month. Age 40: $238–$330/month. Age 45: $374–$522/month. Age 50: $572–$804/month. Age 55: $874–$1,234/month. These rates vary by carrier — some carriers are significantly more competitive for smokers than others. An independent agent compares all available carriers simultaneously to find the lowest smoker rate for your specific age, coverage amount, and health profile.',
  },
  {
    q: 'How long after quitting smoking can I get non-smoker life insurance rates in Indianapolis?',
    a: 'Most life insurance carriers in Indianapolis reclassify you as a non-smoker after 12 months of not smoking — but the exact timeline varies by carrier. Some carriers require 12 months smoke-free. Others require 24 months. A few carriers require 36–48 months for their best non-smoker rates. The savings are significant: a 40-year-old Indianapolis resident who quits smoking and waits 12–24 months can save $150–$200/month on a $500,000 20-year term policy — over $36,000 in savings over the life of the policy. If you\'ve recently quit, an independent agent can identify which carriers will give you the best rate based on exactly how long you\'ve been smoke-free.',
  },
  {
    q: 'What counts as a smoker for life insurance in Indianapolis?',
    a: 'For life insurance purposes in Indianapolis, "smoker" or "tobacco user" typically includes: cigarettes (any amount), cigars (more than 12 per year for most carriers), pipe tobacco, chewing tobacco, snuff, nicotine patches or gum (some carriers), vaping/e-cigarettes (most carriers now classify vapers as tobacco users), and marijuana (some carriers — varies significantly). Occasional cigar smokers (fewer than 12 per year) may qualify for non-smoker rates with some carriers. Nicotine replacement therapy (patches, gum) is treated differently by different carriers — some classify it as tobacco use, others don\'t. An independent agent knows which carriers are most lenient for your specific tobacco use pattern.',
  },
  {
    q: 'Do I have to disclose that I smoke on a life insurance application in Indianapolis?',
    a: 'Yes — you must disclose tobacco use on a life insurance application in Indianapolis. Life insurance applications ask directly about tobacco and nicotine use, typically for the past 12 months (some carriers ask about the past 3–5 years). Lying about tobacco use on a life insurance application is insurance fraud and can result in: (1) Policy cancellation if discovered during the contestability period (first 2 years). (2) Denial of the death benefit if you die and the carrier discovers you misrepresented your tobacco use. (3) Potential legal consequences. Carriers verify tobacco use through prescription database checks, MIB records, and sometimes cotinine tests (a nicotine metabolite detectable in blood and urine for 3–4 days after smoking). Always disclose your tobacco use honestly — an independent agent will find you the best available rate for your actual situation.',
  },
  {
    q: 'Can I get life insurance as a smoker without a medical exam in Indianapolis?',
    a: 'Yes — smokers in Indianapolis can get life insurance without a medical exam. Simplified issue term life insurance is available for smokers with no blood draw or urine sample — just answer health questions online or by phone. Coverage up to $500,000 is available with no exam. Approval typically takes 24–48 hours. However, simplified issue policies for smokers cost more than fully underwritten policies — the carrier charges a premium for the convenience of no exam. For smokers in good health, a fully underwritten policy (which includes a cotinine test to verify tobacco use) will typically offer lower rates than simplified issue. An independent agent can compare both options for your specific situation.',
  },
  {
    q: 'What is the best life insurance for smokers in Indianapolis?',
    a: 'The best life insurance for smokers in Indianapolis depends on your age, coverage needs, and how long you\'ve been smoking. For young smokers (under 40): Banner Life and Protective Life consistently offer the most competitive smoker rates for term life insurance. For middle-aged smokers (40–55): Pacific Life and Banner Life are typically the best options — compare all carriers as rates vary significantly. For older smokers (55+): Mutual of Omaha and North American Company offer competitive rates, including simplified issue options with no medical exam. For smokers who have recently quit: The best carrier depends on exactly how long you\'ve been smoke-free — some carriers reclassify at 12 months, others at 24 months. An independent agent compares all available carriers simultaneously to find your best rate.',
  },
  {
    q: 'Will my life insurance rates go down if I quit smoking in Indianapolis?',
    a: 'Yes — your life insurance rates can go down significantly if you quit smoking in Indianapolis. However, you typically need to apply for a new policy (or request a rate review) after you\'ve been smoke-free for the required period. Most carriers require 12–24 months smoke-free before reclassifying you as a non-smoker. The savings are substantial: a 40-year-old Indianapolis resident who quits smoking can save $150–$200/month on a $500,000 20-year term policy — over $36,000 in total savings. If you have an existing smoker policy and quit smoking, contact your agent after 12 months smoke-free. We can apply for a rate review or help you replace your policy with a new non-smoker policy at a significantly lower rate.',
  },
  {
    q: 'Does vaping count as smoking for life insurance in Indianapolis?',
    a: 'Yes — most life insurance carriers in Indianapolis classify vaping (e-cigarettes, vape pens, JUUL, etc.) as tobacco use. This means vapers are typically charged smoker rates, which are 2–4x higher than non-smoker rates. However, carrier policies on vaping vary: Some carriers classify vapers as tobacco users and charge full smoker rates. A few carriers offer "vaper" rate classes that are between smoker and non-smoker rates. Some carriers require a cotinine test — if you vape nicotine, you\'ll test positive. If you vape nicotine-free products, some carriers may classify you as a non-smoker. An independent agent knows which carriers are most favorable for vapers and can find you the best available rate for your specific situation.',
  },
  {
    q: 'Can I get life insurance as a smoker with other health conditions in Indianapolis?',
    a: 'Yes — smokers with other health conditions can still get life insurance in Indianapolis. The key is working with an independent agent who knows which carriers are most lenient for your specific combination of tobacco use and health conditions. Common health conditions that can be combined with smoker status: controlled high blood pressure, controlled type 2 diabetes, high cholesterol (controlled), mild asthma, history of anxiety or depression (treated), and many others. For smokers with serious health conditions, simplified issue or guaranteed issue policies may be the best option — no medical exam, approved in 24–48 hours. An independent agent can match your specific health and tobacco profile to the right carrier.',
  },
  {
    q: 'How do I apply for life insurance as a smoker in Indianapolis?',
    a: 'Applying for life insurance as a smoker in Indianapolis is straightforward. Step 1: Call Hoosier Life Insurance at (317) 648-2983 or book a free 15-minute consultation. Step 2: We\'ll ask about your tobacco use (type, frequency, how long), age, coverage needs, and health situation. Step 3: We compare all available carriers simultaneously — some carriers are significantly more competitive for smokers than others. Step 4: We submit your application to the best-fit carrier. For fully underwritten policies, a paramedical exam (including cotinine test) is scheduled at your convenience. For simplified issue, you answer health questions online or by phone. Step 5: Receive your decision — typically 24–48 hours for simplified issue, 2–4 weeks for fully underwritten. Step 6: Coverage begins — your family is protected.',
  },
];

/* ─── Carrier Comparison ─────────────────────────────────────────────────── */

const CARRIERS = [
  {
    name: 'Banner Life',
    amBest: 'A+',
    smokerStrength: 'Best',
    specialty: 'Consistently lowest smoker rates — best for most Indianapolis smokers',
    icon: 'ri-shield-star-line',
    color: '#4A7C59',
  },
  {
    name: 'Protective Life',
    amBest: 'A+',
    smokerStrength: 'Excellent',
    specialty: 'Competitive smoker rates — especially strong for 30-year term',
    icon: 'ri-shield-check-line',
    color: '#4A7C59',
  },
  {
    name: 'Pacific Life',
    amBest: 'A+',
    smokerStrength: 'Very Good',
    specialty: 'Strong for middle-aged smokers — good rates for ages 40–55',
    icon: 'ri-heart-pulse-line',
    color: '#D97642',
  },
  {
    name: 'Transamerica',
    amBest: 'A',
    smokerStrength: 'Good',
    specialty: 'Best for recent quitters — may reclassify at 2 years smoke-free',
    icon: 'ri-user-heart-line',
    color: '#D97642',
  },
  {
    name: 'Mutual of Omaha',
    amBest: 'A+',
    smokerStrength: 'Good',
    specialty: 'Best for older smokers and simplified issue — no-exam options available',
    icon: 'ri-home-heart-line',
    color: '#D97642',
  },
  {
    name: 'North American Company',
    amBest: 'A+',
    smokerStrength: 'Good',
    specialty: 'Best for smokers with health conditions — lenient underwriting',
    icon: 'ri-calendar-check-line',
    color: '#D97642',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */

const TESTIMONIALS = [
  {
    name: 'David & Michelle K.',
    location: 'Southport, Indianapolis',
    quote: 'Both smokers, both in our 40s. We thought life insurance would be unaffordable. Benjamin found us $500K each for $238/month combined. We had no idea it was that accessible. Our kids are protected.',
    stars: 5,
    coverage: '$500K each',
    status: 'Current smokers',
  },
  {
    name: 'Robert T.',
    location: 'Beech Grove, Indianapolis',
    quote: 'Quit smoking 14 months ago. Benjamin found a carrier that reclassified me as a non-smoker at 12 months. Saved $142/month vs. my old smoker policy. That\'s $1,700 a year back in my pocket.',
    stars: 5,
    coverage: '$400K 20-yr term',
    status: 'Quit 14 months ago',
  },
  {
    name: 'Angela M.',
    location: 'Warren Township, Indianapolis',
    quote: 'I vape and was told I\'d pay smoker rates everywhere. Benjamin found a carrier that treats vapers differently — saved me $80/month vs. the first quote I got. Couldn\'t believe the difference.',
    stars: 5,
    coverage: '$300K 20-yr term',
    status: 'Vaper',
  },
  {
    name: 'Thomas & Linda H.',
    location: 'Pike Township, Indianapolis',
    quote: 'Occasional cigar smokers — a few times a year. Benjamin found us a carrier that classifies us as non-smokers. Saved us both over $100/month each. Huge difference for occasional use.',
    stars: 5,
    coverage: '$350K each',
    status: 'Occasional cigar smokers',
  },
];

/* ─── Quit Timeline ──────────────────────────────────────────────────────── */

const QUIT_TIMELINE = [
  { period: 'Currently smoking', rateClass: 'Tobacco/Smoker', multiplier: '2–4x non-smoker rates', savings: 'Baseline smoker rate' },
  { period: 'Quit < 12 months', rateClass: 'Tobacco/Smoker', multiplier: '2–4x non-smoker rates', savings: 'Still classified as smoker' },
  { period: 'Quit 12–24 months', rateClass: 'Tobacco (some carriers reclassify)', multiplier: '1.5–2x non-smoker rates', savings: 'Some carriers offer better rates' },
  { period: 'Quit 24–48 months', rateClass: 'Non-Tobacco (Standard)', multiplier: '1.1–1.3x non-smoker rates', savings: 'Most carriers reclassify as non-smoker' },
  { period: 'Quit 4+ years', rateClass: 'Non-Tobacco (Preferred)', multiplier: 'Same as non-smoker', savings: 'Full non-smoker rates — maximum savings' },
];

/* ─── Component ─────────────────────────────────────────────────────────── */

export default function LifeInsuranceSmokersIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Rate Finder state
  const [smokerAge, setSmokerAge] = useState<SmokerAge>('40');
  const [smokerCoverage, setSmokerCoverage] = useState<SmokerCoverage>('500k');
  const [smokerTerm, setSmokerTerm] = useState<SmokerTerm>('20');
  const [quitStatus, setQuitStatus] = useState<QuitStatus>('current');
  const [rateResults, setRateResults] = useState<SmokerRateResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    const title = 'Life Insurance for Smokers Indianapolis IN | Best Smoker Rates | Hoosier Life Insurance';
    const description = 'Life insurance for smokers in Indianapolis, IN. Compare smoker rates from 20+ carriers. Use our Smoker Rate Finder. Quit smoking? See how much you can save. Call (317) 648-2983.';
    const keywords = 'life insurance smokers Indianapolis, smoker life insurance Indianapolis, tobacco life insurance Indianapolis Indiana, life insurance rates smokers Indianapolis';

    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/e83f4381817fcf8c902e1c834c371b52.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
      { name: 'ICBM', content: '39.7684, -86.1581' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
        about: [
          { '@type': 'Thing', name: 'Life Insurance for Smokers Indianapolis' },
          { '@type': 'Thing', name: 'Smoker Life Insurance Rates Indianapolis' },
          { '@type': 'Thing', name: 'Tobacco Life Insurance Indianapolis Indiana' },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Smokers Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Life Insurance for Smokers',
        name: 'Life Insurance for Smokers Indianapolis',
        description: 'Life insurance for smokers and tobacco users in Indianapolis and Marion County. Compare smoker rates from 20+ carriers. Independent agent — no bias, no quotas.',
        url: CANONICAL,
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          telephone: '+1-317-648-2983',
          url: SITE_URL,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '5625 German Church Rd',
            addressLocality: 'Indianapolis',
            addressRegion: 'IN',
            postalCode: '46235',
            addressCountry: 'US',
          },
          geo: { '@type': 'GeoCoordinates', latitude: 39.7684, longitude: -86.1581 },
          openingHoursSpecification: [
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
            { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '08:00', closes: '13:00' },
          ],
        },
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedIn: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Carmel' },
          { '@type': 'City', name: 'Fishers' },
          { '@type': 'City', name: 'Noblesville' },
          { '@type': 'State', name: 'Indiana' },
        ],
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127',
          bestRating: '5',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SpeakableSpecification',
        cssSelector: ['#smokers-stats-bar', '#smokers-key-facts'],
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const handleFindRates = () => {
    const results = getSmokerRates(smokerAge, smokerCoverage, smokerTerm, quitStatus);
    setRateResults(results);
  };

  const ageOptions: SmokerAge[] = ['25', '30', '35', '40', '45', '50', '55', '60'];
  const coverageOptions: { value: SmokerCoverage; label: string }[] = [
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
    { value: '750k', label: '$750K' },
    { value: '1m', label: '$1M' },
  ];
  const termOptions: SmokerTerm[] = ['10', '15', '20', '25', '30'];
  const quitOptions: { value: QuitStatus; label: string; desc: string }[] = [
    { value: 'current', label: 'Current Smoker', desc: 'Still smoking' },
    { value: 'quit1yr', label: 'Quit < 1 Year', desc: 'Recently quit' },
    { value: 'quit2yr', label: 'Quit 1–3 Years', desc: 'Some savings possible' },
    { value: 'quit4yr', label: 'Quit 4+ Years', desc: 'Non-smoker rates!' },
    { value: 'never', label: 'Never Smoked', desc: 'Best rates (comparison)' },
  ];

  const lowestRate = rateResults ? Math.min(...rateResults.map(r => r.monthlyRate)) : 0;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for Smokers</span>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[580px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20couple%20in%20their%2040s%20sitting%20together%20at%20modern%20kitchen%20table%20reviewing%20life%20insurance%20documents%20on%20laptop%20life%20insurance%20for%20smokers%20concept%20warm%20contemporary%20home%20environment%20Marion%20County%20Indiana%20natural%20light%20relaxed%20confident%20financial%20planning%20family%20protection%20photorealistic%20warm%20neutral%20tones&width=1440&height=700&seq=smokers-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance for Smokers Indianapolis Indiana — Best Smoker Rates"
            title="Life Insurance for Smokers in Indianapolis, Indiana — Smoker Rate Finder"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-shield-check-line text-sm"></i>
              Indianapolis &amp; Marion County · Smoker Life Insurance · 20+ Carriers Compared
            </span>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance for Smokers —{' '}
              <span className="text-[#D97642]">Best Tobacco Rates in Indianapolis</span>
            </h1>

            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Smokers can get life insurance in Indianapolis — and some carriers offer <strong className="text-white">significantly better rates than others</strong>. Use our Smoker Rate Finder to compare all available carriers and find your lowest rate.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '20+', label: 'Carriers compared for smokers' },
                { value: '2–4x', label: 'Typical smoker rate vs. non-smoker' },
                { value: '12 mo', label: 'Quit time for non-smoker rates (some carriers)' },
                { value: 'Free', label: 'Smoker rate comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="#rate-finder"
                className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-search-line text-lg"></i>
                Find My Smoker Rate
              </a>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
              <button
                onClick={() => {
                  const widget = document.querySelector('readdy-agent') as HTMLElement & { open?: () => void };
                  if (widget && typeof widget.open === 'function') {
                    widget.open();
                  } else {
                    const btn = document.querySelector('.readdy-agent-button') as HTMLElement;
                    if (btn) btn.click();
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-4 bg-white/15 hover:bg-white/25 border border-white/30 hover:border-white/50 text-white text-base font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                <i className="ri-chat-3-line text-lg"></i>
                Chat with us
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section id="smokers-stats-bar" className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '~19%', label: 'Of Indiana adults smoke — you\'re not alone, and you can get coverage' },
              { value: '2–4x', label: 'Smoker rates vs. non-smoker — but still very affordable for most' },
              { value: '$142/mo', label: 'Average savings when a 40-yr-old Indianapolis smoker quits and reclassifies' },
              { value: '20+', label: 'Carriers compared — some are significantly better for smokers than others' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section id="smokers-key-facts" className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Life Insurance for Smokers in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Smokers can get life insurance in Indianapolis — every major carrier offers coverage to tobacco users',
                  'Smoker rates are 2–4x higher than non-smoker rates — but still affordable for most Indianapolis residents',
                  'Carrier rates for smokers vary significantly — some carriers are 30–40% cheaper than others for the same coverage',
                  'Quit smoking for 12–24 months and most carriers reclassify you as a non-smoker — saving $100–$200/month',
                  'Vapers and e-cigarette users are typically classified as tobacco users by most Indianapolis carriers',
                  'Occasional cigar smokers (fewer than 12/year) may qualify for non-smoker rates with some carriers',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT + SIDEBAR ───────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT COLUMN */}
          <div>

            {/* ── SMOKER RATE FINDER ────────────────────────────────────── */}
            <div id="rate-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Smoker Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Find Your Best Smoker Life Insurance Rate — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, term, and smoking status to see estimated rates from the top carriers for Indianapolis smokers — ranked lowest to highest.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-search-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button
                          key={a}
                          onClick={() => { setSmokerAge(a); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${smokerAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setSmokerCoverage(opt.value); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${smokerCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Term */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Term Length</label>
                    <div className="flex flex-wrap gap-2">
                      {termOptions.map(t => (
                        <button
                          key={t}
                          onClick={() => { setSmokerTerm(t); setRateResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${smokerTerm === t ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          {t}-yr
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quit Status */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Smoking Status</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {quitOptions.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { setQuitStatus(opt.value); setRateResults(null); }}
                          className={`px-4 py-3 rounded-lg text-left transition-all cursor-pointer ${quitStatus === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
                        >
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${quitStatus === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleFindRates}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base"
                  >
                    <i className="ri-search-line text-lg"></i>
                    Find My Best Smoker Rate
                  </button>
                </div>

                {/* Results */}
                {rateResults !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Smoker Rates — ${COVERAGE_MAP[smokerCoverage].toLocaleString()} {smokerTerm}-Year Term · Age {smokerAge}
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${lowestRate}/mo
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Rate class: <strong>{rateResults[0].rateClass}</strong></p>
                    <p className="text-xs text-gray-400 mb-6">{rateResults[0].notes}</p>

                    <div className="flex flex-col gap-3 mb-6">
                      {rateResults.map((result, i) => (
                        <div
                          key={result.carrier}
                          className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>
                                {i + 1}
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{result.carrier}</span>
                                  {result.badge && i === 0 && (
                                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{result.badge}</span>
                                  )}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {result.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">{result.rateClass}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${result.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${result.annualRate.toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {quitStatus === 'current' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                        <div className="flex items-start gap-3">
                          <i className="ri-lightbulb-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
                          <div>
                            <p className="text-sm font-semibold text-amber-800 mb-1">Thinking about quitting?</p>
                            <p className="text-xs text-amber-700">Switch to "Quit 4+ Years" above to see how much you could save. Most 40-year-old Indianapolis smokers save $100–$200/month after reclassification.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates based on general carrier rate tables for Indianapolis, IN. Actual rates depend on full underwriting, your specific health profile, and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized quote.
                      </p>
                    </div>

                    <button
                      onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                    >
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Smoker Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {rateResults === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-search-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, term, and smoking status above, then click <strong className="text-[#1A3A52]">Find My Best Smoker Rate</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quit Timeline */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Quit &amp; Save</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How Quitting Smoking Affects Your Life Insurance Rates in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Quitting smoking is one of the most powerful ways to reduce your life insurance premiums. Here's exactly how the timeline works for Indianapolis residents:
              </p>

              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4">
                  <h3 className="text-sm font-bold text-white">Quit Smoking Rate Timeline — Indianapolis Life Insurance</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {QUIT_TIMELINE.map((row, i) => (
                    <div key={i} className={`px-6 py-5 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full flex-shrink-0 ${i === 0 ? 'bg-red-100' : i === 4 ? 'bg-[#4A7C59]/20' : 'bg-[#D97642]/10'}`}>
                          <i className={`text-lg ${i === 0 ? 'ri-close-circle-line text-red-400' : i === 4 ? 'ri-checkbox-circle-fill text-[#4A7C59]' : 'ri-time-line text-[#D97642]'}`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 flex-wrap mb-1">
                            <span className="text-sm font-bold text-[#1A3A52]">{row.period}</span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${i === 4 ? 'bg-[#4A7C59]/10 text-[#4A7C59]' : i === 0 ? 'bg-red-50 text-red-400' : 'bg-[#D97642]/10 text-[#D97642]'}`}>
                              {row.rateClass}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 flex-wrap">
                            <span className="text-xs text-gray-500">{row.multiplier}</span>
                            <span className="text-xs font-semibold text-[#1A3A52]">{row.savings}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 bg-[#4A7C59]/5 border-t border-[#4A7C59]/20">
                  <p className="text-xs text-[#4A7C59] font-semibold flex items-center gap-2">
                    <i className="ri-information-line"></i>
                    Exact timelines vary by carrier. An independent agent knows which carriers reclassify earliest — potentially saving you money sooner.
                  </p>
                </div>
              </div>
            </div>

            {/* Carrier Comparison */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Carrier Comparison</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Best Life Insurance Carriers for Smokers in Indianapolis
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Not all carriers treat smokers the same. Some are significantly more competitive for tobacco users — and the difference can be $50–$150/month for the same coverage.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {CARRIERS.map((carrier, i) => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${carrier.icon} text-lg text-[#D97642]`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-bold text-[#1A3A52]">{carrier.name}</span>
                        <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {carrier.amBest}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${carrier.smokerStrength === 'Best' ? 'bg-[#4A7C59] text-white' : carrier.smokerStrength === 'Excellent' ? 'bg-[#4A7C59]/20 text-[#4A7C59]' : 'bg-[#D97642]/10 text-[#D97642]'}`}>
                          {carrier.smokerStrength}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed">{carrier.specialty}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Smokers Who Got the Coverage They Needed
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#1A3A52]/10 text-[#1A3A52] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.status}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance for Smokers Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-smokers-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* RIGHT COLUMN — Sticky Sidebar */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">

            {/* Quote CTA */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-shield-check-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Smoker Rate Quote</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Smokers welcome</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No-exam options</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Best rate guaranteed</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a
                    href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold"
                  >
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link
                    to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · Smokers welcome</p>
              </div>
            </div>

            {/* Sample Smoker Rates */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Sample Smoker Rates — $500K 20-Year Term
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { age: 'Age 30', smoker: '$120/mo', nonSmoker: '$30/mo' },
                  { age: 'Age 35', smoker: '$162/mo', nonSmoker: '$40/mo' },
                  { age: 'Age 40', smoker: '$238/mo', nonSmoker: '$58/mo' },
                  { age: 'Age 45', smoker: '$374/mo', nonSmoker: '$94/mo' },
                  { age: 'Age 50', smoker: '$572/mo', nonSmoker: '$143/mo' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-[#1A3A52]">{row.age}</span>
                    <div className="flex gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Smoker</div>
                        <div className="text-xs font-bold text-red-400">{row.smoker}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Non-Smoker</div>
                        <div className="text-xs font-bold text-[#4A7C59]">{row.nonSmoker}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Sample rates for illustration. Actual rates vary by carrier and health profile.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Smoker Rate Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Smoker Rate Call',
                    step0Title: 'Pick a Date',
                    step1Title: 'Choose a Time',
                    step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date',
                    selectTime: 'See Available Times →',
                    reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev',
                    nextMonth: 'Next',
                    noSlotsAvailable: 'No slots available for this date',
                    selectedDateTime: 'Your Appointment',
                    modifyTime: 'Change',
                    customerName: 'Full Name',
                    customerPhone: 'Phone Number',
                    notes: "Tobacco type and any questions? (Optional)",
                    submitButton: 'Confirm My Free Smoker Rate Call',
                    submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.',
                    backButton: '← Back to Calendar',
                    required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Life Insurance', icon: 'ri-timer-flash-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
                  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers — Indianapolis', icon: 'ri-search-line' },
                  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-parent-line' },
                ].map(link => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20male%20insurance%20agent%20in%20his%20late%2030s%20wearing%20a%20charcoal%20blazer%20and%20white%20shirt%20friendly%20confident%20smile%20sitting%20at%20modern%20desk%20with%20laptop%20showing%20life%20insurance%20for%20smokers%20rate%20comparison%20Indianapolis%20Indiana%20office%20natural%20window%20light%20trustworthy%20approachable%20financial%20advisor%20Marion%20County%20Indiana%20professional%20portrait%20photography%20warm%20tones&width=600&height=700&seq=agent-bwright-smokers-indy-2025a&orientation=portrait"
                alt="Benjamin Wright — Life Insurance for Smokers Agent in Indianapolis, Indiana"
                title="Benjamin Wright — Life Insurance for Smokers Specialist in Indianapolis"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl w-full h-[480px] object-cover object-top"
              />
              <div className="absolute bottom-6 right-6 bg-white rounded-xl px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#D97642] rounded-full flex items-center justify-center">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Smokers</div>
                    <div className="text-base font-bold text-[#1A3A52]">Always Welcome</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-user-heart-line"></i>
                Your Local Indianapolis Smoker Life Insurance Specialist
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A3A52] mb-6">Meet Benjamin Wright</h2>
              <p className="text-base text-gray-600 mb-5 leading-relaxed">
                I've helped hundreds of Indianapolis smokers get the life insurance coverage they need — at the best available rate. Some carriers are significantly more competitive for tobacco users than others, and knowing which carrier to use for your specific situation can save you $50–$150/month.
              </p>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                As an independent agent, I compare all available carriers simultaneously — no bias, no quotas. Whether you're a current smoker, a recent quitter, a vaper, or an occasional cigar smoker, I'll find your best available rate and get you covered.
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="px-7 py-4 bg-[#D97642] text-white rounded-lg font-semibold hover:bg-[#C26638] transition-all whitespace-nowrap cursor-pointer"
                >
                  Get My Smoker Rate Quote
                </button>
                <a
                  href="tel:+13176482983"
                  className="px-7 py-4 bg-white border-2 border-gray-200 text-[#1A3A52] rounded-lg font-semibold hover:border-[#D97642] hover:text-[#D97642] transition-all whitespace-nowrap cursor-pointer"
                >
                  Call (317) 648-2983
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Links */}
      <section className="py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3">Explore All Coverage Options</h2>
            <p className="text-sm text-gray-500 max-w-xl mx-auto">Find the right life insurance for your specific situation in Indianapolis</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { to: '/no-exam-life-insurance-indianapolis', icon: 'ri-timer-flash-line', title: 'No Medical Exam Life Insurance', desc: 'Approved in 24–48 hours — no blood draw, no doctor visit, up to $1M coverage' },
              { to: '/affordable-life-insurance-indianapolis', icon: 'ri-money-dollar-circle-line', title: 'Affordable Life Insurance Indianapolis', desc: 'Budget calculator — see exactly what your monthly budget buys from $15/month' },
              { to: '/term-life-insurance-indianapolis', icon: 'ri-calendar-check-line', title: 'Term Life Insurance Indianapolis', desc: 'Level term coverage — the most affordable way to protect your family' },
              { to: '/life-insurance-quotes-indianapolis', icon: 'ri-search-line', title: 'Compare All Carriers', desc: 'Interactive 6-carrier rate comparison — see all options ranked lowest to highest' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white hover:bg-[#1A3A52] hover:text-white border border-gray-200 rounded-xl p-6 transition-all group cursor-pointer"
              >
                <i className={`${link.icon} text-3xl text-[#D97642] group-hover:text-white mb-3 block`}></i>
                <h3 className="font-bold text-base mb-2 text-[#1A3A52] group-hover:text-white">{link.title}</h3>
                <p className="text-sm text-gray-500 group-hover:text-gray-200">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Smokers can get life insurance in Indianapolis — and we'll find your best rate.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Smoker Rate Finder to compare all available carriers, then call for your exact personalized quote. 20+ A-rated carriers compared. No obligation, no pressure.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="#rate-finder"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-lg"></i>
              Find My Smoker Rate
            </a>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Life Insurance for Smokers Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Smoker Life Insurance" quoteHref="/instant-quote" />

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Smoker Life Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe
                src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance"
                style={{ width: '100%', height: '600px', border: 'none' }}
                title="Book Appointment"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
